import React from 'react'
import styled from 'styled-components'
import moment from 'moment'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import TimelapseIcon from '@mui/icons-material/Timelapse'

const ReportingPeriodBanner = ({ settings, stats }) => {
  if (!settings) return null

  const periodStart = moment(settings.periodStart)
  const periodEnd = moment(settings.periodEnd)
  const daysRemaining = settings.daysRemaining
  const periodLabel = settings.reportingPeriod === 'monthly' ? 'Month' : 'Quarter'

  const completedInPeriod = stats?.completedInPeriod || 0
  const outstandingInPeriod = stats?.outstandingInPeriod || 0
  const total = completedInPeriod + outstandingInPeriod
  const completionRate = total > 0 ? Math.round((completedInPeriod / total) * 100) : 0

  const isUrgent = daysRemaining <= 7

  return (
    <BannerWrapper $urgent={isUrgent}>
      <BannerLeft>
        <BannerIcon $urgent={isUrgent}>
          {isUrgent ? <TimelapseIcon /> : <CalendarTodayIcon />}
        </BannerIcon>
        <BannerInfo>
          <BannerTitle>
            {periodLabel}: {periodStart.format('D MMM')}: {periodEnd.format('D MMM YYYY')}
          </BannerTitle>
          <BannerStats>
            {daysRemaining} day{daysRemaining !== 1 ? 's' : ''} remaining
            {total > 0 && ` · ${completedInPeriod}/${total} tasks completed`}
          </BannerStats>
        </BannerInfo>
      </BannerLeft>
      {total > 0 && (
        <ProgressSection>
          <ProgressBarWrapper>
            <ProgressBarFill $progress={completionRate} $urgent={isUrgent} />
          </ProgressBarWrapper>
          <ProgressLabel $urgent={isUrgent}>{completionRate}%</ProgressLabel>
        </ProgressSection>
      )}
    </BannerWrapper>
  )
}

export default ReportingPeriodBanner

const BannerWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 24px;
  background: ${({ $urgent }) => $urgent ? '#fef3c7' : '#f0f9ff'};
  border-bottom: 1px solid ${({ $urgent }) => $urgent ? '#fde68a' : '#bae6fd'};
  gap: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    padding: 12px 16px;
  }
`

const BannerLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
`

const BannerIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: ${({ $urgent }) => $urgent ? '#fbbf24' : '#0ea5e9'};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg { font-size: 18px; }
`

const BannerInfo = styled.div`
  min-width: 0;
`

const BannerTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti || '#0C1229'};
`

const BannerStats = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
  margin-top: 2px;
`

const ProgressSection = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  min-width: 160px;

  @media (max-width: 768px) {
    width: 100%;
    min-width: 0;
  }
`

const ProgressBarWrapper = styled.div`
  flex: 1;
  height: 6px;
  background: rgba(0, 0, 0, 0.08);
  border-radius: 3px;
  overflow: hidden;
  min-width: 100px;
`

const ProgressBarFill = styled.div`
  height: 100%;
  width: ${({ $progress }) => $progress}%;
  background: ${({ $urgent }) => $urgent ? '#f59e0b' : '#0ea5e9'};
  border-radius: 3px;
  transition: width 0.3s ease;
`

const ProgressLabel = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${({ $urgent }) => $urgent ? '#b45309' : '#0369a1'};
  min-width: 35px;
  text-align: right;
`
